import { Amplify, type ResourcesConfig } from "aws-amplify";
import {
	resetPassword as awsResetPassword,
	signIn as awsSignIn,
	signOut as awsSignOut,
	confirmResetPassword,
	confirmSignIn,
	fetchAuthSession,
	getCurrentUser,
	type AuthSession,
} from "aws-amplify/auth";
import { onMounted, ref } from "vue";
import { emptyGUID } from "./general";
import type { Group } from "./user";

const CONNECTION_TIMEOUT = 3000;

export type FlightPathUser = {
	email: string;
	username: string;
	accountId: string;
	emailVerified: boolean;
	groups: string[];
};

export async function getUserFromIdToken(): Promise<FlightPathUser> {
	const session = await fetchAuthSession();
	const idToken = session.tokens?.idToken?.payload;
	if (!idToken) throw new Error("No id token found");

	return {
		email: idToken.email as string,
		username: idToken["cognito:username"] as string,
		accountId: idToken["custom:accountid"] as string,
		emailVerified: idToken.email_verified as boolean,
		groups: idToken["cognito:groups"] as string[],
	};
}

export function configureAWS(config: ResourcesConfig) {
	Amplify.configure(config);
}

export async function getToken(type: "id" | "access" = "id"): Promise<string> {
	const { session } = await fetchAuthSessionTimeout();
	const tokens = session?.tokens;

	return type === "id"
		? (tokens?.idToken?.toString() ?? "")
		: (tokens?.accessToken?.toString() ?? "");
}

export async function SignedIn(): Promise<boolean> {
	try {
		const user = await getCurrentUser();
		return user != undefined;
	} catch (error) {
		console.log("Not logged in");
		return false;
	}
}

export async function attachIotPolicy(apiUrl: string): Promise<string> {
	const authSession = await fetchAuthSession();
	const token = authSession.tokens?.idToken?.toString();
	if (!token) {
		throw new Error("No id token found");
	}
	const response = await fetch(`${apiUrl}/attach-iot-policy`, {
		method: "POST",
		headers: {
			Authorization: "Bearer " + token,
		},
		body: authSession.identityId,
	});
	if (!response.ok) {
		throw new Error(`Failed to attach IoT policy: ${response.status}`);
	}

	return token;
}

export function useAuth() {
	const user = ref<FlightPathUser | null>(null);

	async function signIn(
		username: string,
		password: string,
		apiUrl: string,
	): Promise<{
		user?: FlightPathUser;
		completeNewPassword?: (
			password: string,
			apiUrl: string,
		) => Promise<void>;
	}> {
		try {
			const result = await Promise.race([
				awsSignIn({ username, password }),
				new Promise<never>((_, reject) =>
					setTimeout(
						() =>
							reject(
								new Error(
									"Sign-in operation timed out. Please check your internet connection and try again.",
								),
							),
						CONNECTION_TIMEOUT,
					),
				),
			]);
			if (
				result.nextStep.signInStep ===
				"CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
			) {
				return {
					completeNewPassword: async (password, apiUrl) => {
						await confirmSignIn({
							challengeResponse: password,
						});
						const token = await attachIotPolicy(apiUrl);
						user.value = await getUserFromIdToken();
						const responseActivate = await fetch(
							`${apiUrl}/cloud-api/umc/user/${user.value.email}/activate`,
							{
								method: "PUT",
								headers: {
									Authorization: "Bearer " + token,
								},
							},
						);
						if (!responseActivate.ok) {
							throw new Error(
								`Failed to Activate user: ${responseActivate.status}`,
							);
						}
					},
				};
			} else {
				await attachIotPolicy(apiUrl);
				user.value = await getUserFromIdToken();
				return { user: user.value };
			}
		} catch (error) {
			console.error(`Sign in error: ${error}`);
			throw error;
		}
	}

	async function signOut() {
		try {
			await awsSignOut();
			user.value = null;
		} catch (error) {
			console.log("error signing out: ", error);
		}
	}

	async function forgotPassword(username: string) {
		try {
			await awsResetPassword({ username });
			return async (confirmationCode: string, newPassword: string) => {
				try {
					await confirmResetPassword({
						username,
						confirmationCode,
						newPassword,
					});
				} catch (error) {
					console.error(error);
					throw error;
				}
			};
		} catch (error) {
			console.error(error);
			throw error;
		}
	}

	async function resetPassword(
		username: string,
		confirmationCode: string,
		newPassword: string,
	) {
		try {
			await confirmResetPassword({
				username,
				confirmationCode,
				newPassword,
			});
		} catch (error) {
			console.error(error);
			throw error;
		}
	}

	onMounted(async () => {
		try {
			user.value = await getUserFromIdToken();
		} catch (error) {
			console.log("Not logged in");
		}
	});

	return { signIn, signOut, user, forgotPassword, resetPassword };
}
const customerId = ref(emptyGUID);
const originalCustomerId = ref(emptyGUID);

export async function setCustomerId(
	isLocal: boolean,
	offlineSession?: AuthSession | null,
) {
	if (!isLocal) {
		try {
			let session = null;
			const { session: result, sessionFetched } =
				await fetchAuthSessionTimeout();
			if (!sessionFetched) {
				session = offlineSession;
			} else {
				session = result;
			}
			if (!session?.tokens?.idToken) return false;
			originalCustomerId.value = customerId.value = session.tokens
				?.idToken?.payload["custom:accountid"] as string;
			customerId.value = originalCustomerId.value;
		} catch {
			return false;
		}
	}

	return true;
}

export async function setOrganizationId(
	organizationId: string,
	offlineSession?: AuthSession | null,
) {
	try {
		let session = null;
		const { session: result, sessionFetched } =
			await fetchAuthSessionTimeout();
		if (!sessionFetched) {
			session = offlineSession;
		} else {
			session = result;
		}
		if (!session?.tokens?.idToken) return false;
		originalCustomerId.value = customerId.value = session.tokens?.idToken
			?.payload["custom:accountid"] as string;
		customerId.value = organizationId;
	} catch {
		return false;
	}

	return true;
}

export function useCustomerId() {
	return customerId.value;
}

export function useCustomerIdRef() {
	return customerId;
}

export function useOriginalCustomerId() {
	return originalCustomerId.value;
}

export function useOriginalCustomerIdRef() {
	return originalCustomerId;
}

export async function isUserFlightPathAdmin(): Promise<boolean> {
	try {
		const session = await fetchAuthSession();
		if (!session.tokens?.idToken) return false;
		const idToken = session.tokens?.idToken?.payload;
		const groups = idToken["cognito:groups"] as string[];
		return groups.includes("FlightPathAdmin");
	} catch (error) {
		console.error(error);
		return false;
	}
}

export async function isUserGroupAuthorized(
	authorizedGroups: string[],
	offlineSession?: AuthSession | null,
): Promise<boolean> {
	try {
		let session = null;
		const { session: result, sessionFetched } =
			await fetchAuthSessionTimeout();
		if (!sessionFetched) {
			session = offlineSession;
		} else {
			session = result;
		}
		if (!session?.tokens?.idToken) return false;

		const idToken = session.tokens.idToken.payload;
		const groups = idToken["cognito:groups"] as string[];

		return groups.some(group => authorizedGroups.includes(group));
	} catch (error) {
		console.error(error);
		return false;
	}
}

export async function isUserGroupsAuthorized(
	...authorizedGroups: string[][] // Rest parameter for arrays of strings
): Promise<boolean> {
	// Flatten the authorizedGroups array of arrays into a single array
	const flattenedAuthorizedGroups = authorizedGroups.flat();

	// Use the isUserGroupAuthorized function with the flattened array
	return isUserGroupAuthorized(flattenedAuthorizedGroups);
}

export function useUserGroupAuthorized(authorizedGroups: Group[]) {
	const isAuthorized = ref(false);

	onMounted(async () => {
		isAuthorized.value = await isUserGroupAuthorized(authorizedGroups);
	});

	return isAuthorized;
}

export async function fetchAuthSessionTimeout() {
	let sessionFetched = false;

	let session: AuthSession | null = null;
	try {
		session = await Promise.race([
			(async () => {
				const result = await fetchAuthSession();
				sessionFetched = true;
				return result;
			})(),
			new Promise<null>(resolve =>
				setTimeout(() => resolve(null), CONNECTION_TIMEOUT),
			),
		]);

		return { session, sessionFetched };
	} catch (e: unknown) {
		return { session: null, sessionFetched: false };
	}
}
